/**
 * SmarterElements
 * Core module for embeddable UI components
 * Uses iframes loading routes from the React application
 */

import { createJWT } from './utils/jwt';
import { createConfig, Environment } from '@platform-shared/config';

// Default styles for the iframe container
const DEFAULT_IFRAME_STYLES: Record<string, string> = {
  border: '0',
  margin: '0',
  padding: '0',
  width: '100%',
  height: '100%',
  overflow: 'hidden',
  display: 'block',
  background: 'transparent',
};

/**
 * Get the base URL for a specific environment from the shared configuration
 * @param env The environment name
 */
function getElementsBaseUrl(env: Environment): string {
  const config = createConfig(env);
  return config['elements']?.ui?.baseUrl || '';
}

// Create a record of environment URLs using the shared configuration
const ENVIRONMENT_URLS: Record<Environment, string> = {
  production: getElementsBaseUrl('production'),
  development: getElementsBaseUrl('development'),
  stage: getElementsBaseUrl('stage'),
  local: getElementsBaseUrl('local')
};

// Note: Keeping this for future use
// const DEFAULT_CONTAINER_STYLES: Record<string, string> = {
//   position: 'relative',
//   width: '100%',
//   height: '100%',
//   overflow: 'hidden',
// };

interface SmarterElementsOptions {
  publicApiKey?: string | null;
  environment?: Environment;
  baseUrl?: string;
  [key: string]: unknown;
}

interface ElementOptions {
  [key: string]: unknown;
}

type EventHandler = (data?: unknown) => void;

export class SmarterElements {
  options: SmarterElementsOptions;
  private _mountedElements: Map<string, ElementInstance>;

  constructor(options: SmarterElementsOptions = {}) {
    this.options = {
      publicApiKey: null,
      environment: options.environment || 'production',
      ...options,
    };

    // if no baseUrl is provided, use the default based on the environment
    if(!options?.baseUrl){
      const env = this.options.environment as Environment;
      this.options.baseUrl = ENVIRONMENT_URLS[env];
    }

    this._mountedElements = new Map();
  }

  /**
   * Create an element instance
   * @param type - Element type (route name)
   * @param options - Element options
   * @returns ElementInstance
   */
  create(type: string, options: ElementOptions = {}): ElementInstance {
    // Generate a random hash for the element ID (about 10 chars)
    const timestamp = Date.now().toString(36);
    const random = Math.random().toString(36).substring(2, 6);
    const randomHash = timestamp.slice(-6) + random;
    const elementId = `smarter-element-${randomHash}`;
    
    const instance = new ElementInstance(elementId, type, {
      ...this.options,
      ...options,
    });
    this._mountedElements.set(elementId, instance);
    return instance;
  }

  /**
   * Track a mounted element instance
   * @param elementId - The ID of the mounted element
   * @param instance - The element instance
   */
  trackMountedElement(elementId: string, instance: ElementInstance): void {
    this._mountedElements.set(elementId, instance);
  }
}

export class ElementInstance {
  elementId: string;
  type: string;
  options: Record<string, unknown>;
  container: HTMLElement | null;
  iframe: HTMLIFrameElement | null;
  iframeLoaded: boolean;
  private _eventHandlers: Record<string, EventHandler[]>;
  private _pendingUpdates: Record<string, unknown> | null;
  // @ts-ignore - Used in _setupResizeHandling
  private _lastResizeCheck: number = 0;
  private _lastResizeRequest: number = 0;
  private _initialHeight: number = 0;
  private _pendingResizeRequest: boolean = false;
  private _resizeCheckCount: number = 0;
  // @ts-ignore - Used in _setupResizeHandling
  private _heightUpdateInterval: NodeJS.Timeout | null = null;
  // @ts-ignore - Used in _updateIframeHeight
  private _lastHeight: number = 0;
  private _cleanupResizeHandling: (() => void) | null = null;
  private _environment: Environment;


  constructor(elementId: string, type: string, options: Record<string, unknown> = {}) {
    this.elementId = elementId;
    this.type = type;
    this.options = options;
    this.container = null;
    this._environment = (options['environment'] as Environment) || 'production';
    this.iframe = null;
    this.iframeLoaded = false;
    this._eventHandlers = {};
    this._pendingUpdates = null;
    this._initialHeight = 0;
  }

  /**
   * Mount the element to a DOM container
   * @param container - DOM element or selector
   */
  mount(container: HTMLElement | string): void {
    // Implementation of mount method
    if (typeof container === 'string') {
      const element = document.querySelector(container);
      if (!element) {
        throw new Error(`Container element not found: ${container}`);
      }
      this.container = element as HTMLElement;
    } else {
      this.container = container;
    }

    this._loadElementRoute();
  }

  /**
   * Load the element route in the iframe
   * @private
   */
  private _loadElementRoute(): void {
    if (!this.container) return;

    // Create iframe
    this.iframe = document.createElement('iframe');
    Object.assign(this.iframe.style, DEFAULT_IFRAME_STYLES);
    this.iframe.setAttribute('title', `SmarterElements: ${this.type}`);
    this.iframe.setAttribute('allow', 'camera; microphone; fullscreen');
    this.iframe.setAttribute('id', `${this.elementId}`);
    this.iframe.setAttribute('sandbox', 'allow-scripts allow-same-origin allow-forms allow-popups allow-modals allow-orientation-lock allow-presentation');

    // Set initial height based on container dimensions if available
    if (this.container) {
      const containerHeight = this.container.offsetHeight;
      if (containerHeight > 0) {
        // Override the percentage height with a pixel value
        this._initialHeight = containerHeight;
        this.iframe.style.height = `${containerHeight}px`;
        console.log('Setting initial iframe height from container:', containerHeight);
      }
    }

    // Set up resize handling before the iframe loads
    this._setupResizeHandling();

    // Set up message event listener for other messages
    const messageHandler = (event: MessageEvent) => {
      if (!event.data || event.data.source !== 'smarter-elements') return;
      if (event.data.elementId !== this.elementId) return;

      const { type, payload } = event.data;
      this.trigger(type, payload);
    };

    window.addEventListener('message', messageHandler);

    // Set up load handler
    const loadHandler = () => {
      this.iframeLoaded = true;
      this._onReady();
      if (this._pendingUpdates) {
        this.update(this._pendingUpdates);
        this._pendingUpdates = null;
      }
    };

    this.iframe.addEventListener('load', loadHandler, { once: true });

    // Set up error handler
    const errorHandler = (error: ErrorEvent) => {
      console.error('SmarterElements iframe error:', error);
      this.trigger('error', { error: error.message || 'Unknown error occurred' });
    };

    this.iframe.addEventListener('error', errorHandler as EventListener);

    // Set up cleanup function
    const cleanup = () => {
      window.removeEventListener('message', messageHandler);
      this.iframe?.removeEventListener('load', loadHandler as EventListener);
      this.iframe?.removeEventListener('error', errorHandler as EventListener);
    };

    // Append iframe to container
    this.container.innerHTML = '';
    this.container.appendChild(this.iframe);

    // Set up unload cleanup
    const unloadHandler = () => {
      cleanup();
      window.removeEventListener('unload', unloadHandler);
    };
    window.addEventListener('unload', unloadHandler);

    // Set initial source
    this.iframe.src = this._getElementUrl();
  }

  /**
   * Get the URL for the element
   * @private
   */
  private _getElementUrl(): string {
    const baseUrl = (this.options['baseUrl'] as string) || '';
    
    const payload: Record<string, unknown> & { elementId: string } = {
      elementId: this.elementId
    };

    // Add other options to the payload
    Object.entries(this.options).forEach(([key, value]) => {
      // Skip baseUrl and other internal options
      if (key !== 'baseUrl' && key !== 'environment' && key !== 'apiKey') {
        payload[key] = value;
      }
    });

    payload['_initialHeight'] = this._initialHeight;
    // add teh current environment from the instance
    payload['_environment'] = this._environment;
    // Create JWT token
    const token = createJWT(payload);
    
    return `${baseUrl}/elements/${this.type}?data=${token}`;
  }

  /**
   * Called when the element is ready
   * @private
   */
  private _onReady(): void {
    this.trigger('ready');
  }

  /**
   * Set up resize handling for the iframe
   * @private
   */
private _setupResizeHandling(): void {
    if (!this.iframe) return;

    // Listen for messages from iframe
    window.addEventListener('message', event => {
      
      // Make sure the message is from our iframe
      if (this.iframe && event.source === this.iframe.contentWindow) {
        // Handle height updates
        if (event.data.type === 'resize' || event.data.type === 'height') {
          //console.log('Resize event received:', event.data)
          this._updateIframeHeight(event.data.height);
        }

        // Handle events from the element
        if (event.data.type === 'event') {
          this.trigger(event.data.event, event.data.data);
        }
      }
    });

    // Initialize resize tracking variables
    this._lastResizeCheck = Date.now();
    this._lastResizeRequest = 0;
    this._pendingResizeRequest = false;
    this._resizeCheckCount = 0;
  
    // Request an immediate resize when the iframe loads
    this.iframe.addEventListener('load', () => {
      // Small delay to ensure content has rendered
      setTimeout(() => {
        if (this.iframe && this.iframe.contentWindow) {
          console.log('Requesting initial resize after iframe load');
          this.iframe.contentWindow.postMessage(
            {
              type: 'resize',
              elementId: this.elementId,
            },
            '*'
          );
        }
      }, 100);
    });

    // Set up interval to check for height changes (fallback method)
    // Using a much longer interval and implementing both throttling and debouncing
    this._heightUpdateInterval = setInterval(() => {
      this._resizeCheckCount++;

      // Only check every 5th time (effectively every 5 seconds with a 1s interval)
      // This dramatically reduces the number of resize checks
      if (this._resizeCheckCount % 5 === 0) {
        const now = Date.now();

        // Only request a resize if:
        // 1. At least 3 seconds have passed since the last resize request
        // 2. We don't have a pending resize request already
        if (now - this._lastResizeRequest > 3000 && !this._pendingResizeRequest) {
          this._lastResizeCheck = now;
          this._lastResizeRequest = now;
          this._pendingResizeRequest = true;

          if (this.iframe && this.iframe.contentWindow) {
            this.iframe.contentWindow.postMessage(
              {
                type: 'resize',
                elementId: this.elementId,
              },
              '*'
            );

            // Reset the pending flag after a delay (debouncing)
            setTimeout(() => {
              this._pendingResizeRequest = false;
            }, 1000);
          }
        }
      }
    }, 1000);
  }

  /**
   * Update the iframe height
   * @param height - New height in pixels
   * @private
   */
  private _updateIframeHeight(height: number): void {
    if (this.iframe) {
      // Minimum height to prevent collapse
      const minHeight = 60;
      let newHeight = Math.max(height, minHeight);
      const adjustment = 1;

      // Get current height (without 'px' suffix)
      let currentHeight = parseInt(this.iframe.style.height, 10) || 0;

      newHeight = newHeight + adjustment;
      currentHeight = currentHeight + adjustment;

      // Log the height update details
      /*
      console.log('HEIGHT_DEBUG - Parent received height update:', {
        receivedHeight: height,
        calculatedNewHeight: newHeight,
        currentHeight,
        minHeight,
        elementId: this.elementId
      });
      */

      // Only update and trigger event if height actually changed
      if (currentHeight !== newHeight) {
        /*
        console.log('HEIGHT_DEBUG - Updating iframe height:', {
          receivedHeight: height,
          minHeight,
          calculatedHeight: newHeight,
          currentHeight,
          iframeStyle: {
            height: this.iframe.style.height,
            overflow: this.iframe.style.overflow,
            border: this.iframe.style.border,
            margin: this.iframe.style.margin,
            padding: this.iframe.style.padding
          }
        });
        */

        

        // Apply the height with a smooth transition
        if (!this.iframe.style.transition) {
          this.iframe.style.transition = 'height 0.2s ease-in-out';
        }
        this.iframe.style.height = `${newHeight}px`;

        // Store the last height to compare for future updates
        this._lastHeight = newHeight;

        // Trigger an event that consumers can listen for
        this.trigger('resize', { height: newHeight, elementId: this.elementId });
      } else {
        //console.debug(`ElementInstance: Height unchanged at ${newHeight}px, skipping resize event`);
      }
    }
  }

  /**
   * Update element options
   * @param options - New options
   */
  update(options: Record<string, unknown>): void {
    this.options = { ...this.options, ...options };
    
    if (!this.iframeLoaded) {
      this._pendingUpdates = options;
      return;
    }

    if (this.iframe?.contentWindow) {
      this.iframe.contentWindow.postMessage(
        {
          source: 'smarter-elements',
          type: 'update',
          elementId: this.elementId,
          options: this.options,
        },
        '*'
      );
    }
  }

  /**
   * Unmount the element
   */
  unmount(): void {
    if (this.container && this.iframe) {
      // Clean up resize handling
      if (this._cleanupResizeHandling) {
        this._cleanupResizeHandling();
        this._cleanupResizeHandling = null;
      }
      
      this.container.removeChild(this.iframe);
      this.iframe = null;
      this.iframeLoaded = false;
      this.trigger('unmount');
    }
  }

  /**
   * Add event listener
   * @param event - Event name
   * @param handler - Event handler
   */
  on(event: string, handler: EventHandler): void {
    if (!this._eventHandlers[event]) {
      this._eventHandlers[event] = [];
    }
    this._eventHandlers[event].push(handler);
  }

  /**
   * Remove event listener
   * @param event - Event name
   * @param handler - Event handler to remove
   */
  off(event: string, handler: EventHandler): void {
    if (!this._eventHandlers[event]) return;
    
    const index = this._eventHandlers[event].indexOf(handler);
    if (index > -1) {
      this._eventHandlers[event].splice(index, 1);
    }
  }

  /**
   * Trigger event
   * @param event - Event name
   * @param data - Event data
   */
  trigger(event: string, data?: unknown): void {
    if (!this._eventHandlers[event]) return;
    
    this._eventHandlers[event].forEach((handler) => {
      try {
        handler(data);
      } catch (error) {
        console.error(`Error in ${event} handler:`, error);
      }
    });
  }

  /**
   * Render the component
   * @private
   */
  // Marked as private and unused for now
  // private render(): void {
  //   if (!this.iframeLoaded) return;
    
  //   if (this.iframe?.contentWindow) {
  //     this.iframe.contentWindow.postMessage(
  //       {
  //         source: 'smarter-elements',
  //         type: 'render',
  //         elementId: this.elementId,
  //         options: this.options,
  //       },
  //       '*'
  //     );
  //   }
  // }

  /**
   * Open a modal with the element content
   * @param options - Modal options including width, maxWidth, and maxHeight
   * @returns The element instance for chaining
   */
  openModal(options: {
    width?: string;
    maxWidth?: string;
    maxHeight?: string;
    [key: string]: unknown;
  } = {}): ElementInstance {
    // Create modal container if it doesn't exist
    let modalContainer = document.getElementById('sp-elements-modal-container');
    if (!modalContainer) {
      modalContainer = document.createElement('div');
      modalContainer.id = 'sp-elements-modal-container';
      modalContainer.style.position = 'fixed';
      modalContainer.style.top = '0';
      modalContainer.style.left = '0';
      modalContainer.style.width = '100%';
      modalContainer.style.height = '100%';
      modalContainer.style.backgroundColor = 'rgba(0, 0, 0, 0.5)';
      modalContainer.style.zIndex = '9999';
      modalContainer.style.display = 'flex';
      modalContainer.style.justifyContent = 'center';
      modalContainer.style.alignItems = 'center';
      document.body.appendChild(modalContainer);
      
      // Add event listener to close modal on background click
      modalContainer.addEventListener('click', (event: MouseEvent) => {
        if (event.target === modalContainer) {
          this.closeModal();
        }
      });
    }
    
    // Create modal content container
    const modalContent = document.createElement('div');
    modalContent.style.backgroundColor = 'white';
    modalContent.style.borderRadius = '4px';
    modalContent.style.boxShadow = '0 2px 10px rgba(0, 0, 0, 0.2)';
    modalContent.style.width = options.width || '80%';
    modalContent.style.maxWidth = options.maxWidth || '800px';
    modalContent.style.maxHeight = options.maxHeight || '90%';
    modalContent.style.overflow = 'auto';
    modalContent.style.position = 'relative';
    
    // Add close button
    const closeButton = document.createElement('button');
    closeButton.innerHTML = '&times;';
    closeButton.style.position = 'absolute';
    closeButton.style.top = '10px';
    closeButton.style.right = '10px';
    closeButton.style.border = 'none';
    closeButton.style.background = 'transparent';
    closeButton.style.fontSize = '24px';
    closeButton.style.cursor = 'pointer';
    closeButton.style.zIndex = '1';
    closeButton.addEventListener('click', () => this.closeModal());
    modalContent.appendChild(closeButton);
    
    // Mount the element in the modal
    this.mount(modalContent);
    
    // Add the modal content to the container
    if (modalContainer) {
      modalContainer.innerHTML = '';
      modalContainer.appendChild(modalContent);
    }
    
    // Trigger modal open event
    this.trigger('modal:open', { elementId: this.elementId });
    
    return this;
  }
  
  /**
   * Close the modal
   * @returns The element instance for chaining
   */
  closeModal(): ElementInstance {
    const modalContainer = document.getElementById('sp-elements-modal-container');
    if (modalContainer && modalContainer.parentNode) {
      // Unmount the element
      this.unmount();
      
      // Remove the modal container
      document.body.removeChild(modalContainer);
      
      // Trigger modal close event
      this.trigger('modal:close', { elementId: this.elementId });
    }
    
    return this;
  }
}

/**
 * Create a SmarterElements instance
 * @param options - Configuration options
 * @returns A new SmarterElements instance
 */
export function createSmarterElements(options: SmarterElementsOptions = {}): SmarterElements {
  return new SmarterElements(options);
}

// Export default for backward compatibility
export default createSmarterElements;
